import {
  UploadFileOutlined,
  PageDS,
  Spacer,
  TabMenu,
  ToastContainerDS,
  triggerToastDS,
  dismissToastDS,
  updateToastDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnalyticsVehiclesTab } from "./advancedAnalytics/advancedAnalyticsVehiclesTab";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  driverPlanningsSlice,
  selectDriverPlanningsStatus,
  selectDriverPlanningsUploadProgress,
} from "./driverPlannings/driverPlanningsSlice";
import { ImportDriverPlanningsModal } from "./driverPlannings/modals/importDriverPlanningsModal";
import { ImportContractDataModal } from "./advancedAnalytics/modals/importContractDataModal";
import { HistoryTab } from "./history/historyTab";
import { VehicleSessionsTab } from "./vehicleSessions/vehicleSessionsTab";
import { MonthlyReportsTab } from "./monthlyReports/monthlyReportsTab";
import { GenerateMonthlyReportModal } from "./monthlyReports/modals/generateMonthlyReportModal";
import "../../../i18n/i18n";
import { supportPhonesByCompanyGroupUuid } from "../../../constants/support";
import { selectManagersList } from "../users/usersSlice";
import { selectCompany, selectManager } from "../homeSlice";
import {
  advancedAnalyticVehiclesAsync,
  fleetSlice,
  selectAdvancedAnalyticsVehiclesListStatus,
  selectGenerateMonthlyMileageGapReportStatus,
  selectUpdateVehicleRestitutionStatus,
} from "./fleetSlice";
import {
  resetImportContractDataStatus,
  selectImportContractDataStatus,
  selectUpdatedCount,
} from "./advancedAnalytics/contractFollowUp/vehicleContractDataSlice";

export const Fleet = () => {
  const { t } = useTranslation();
  const firstPath = location.pathname.split("/")[1];
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);
  const manager = useAppSelector(selectManager);
  const managers = useAppSelector(selectManagersList);
  const advancedAnalyticListVehiclesStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );

  const otherSupportPhones = useMemo(
    () =>
      managers && managers.length > 0 && managers[0].companyGroup
        ? supportPhonesByCompanyGroupUuid[managers[0].companyGroup]
        : undefined,
    [managers],
  );

  const [selectedTab, setSelectedTab] = useState<string>(
    params.tab ?? "advanced_analytics",
  );
  const driverPlanningsStatus = useAppSelector(selectDriverPlanningsStatus);
  const generateMonthlyMileageGapReportStatus = useAppSelector(
    selectGenerateMonthlyMileageGapReportStatus,
  );
  const updateVehicleRestitutionStatus = useAppSelector(
    selectUpdateVehicleRestitutionStatus,
  );
  const importContractDataStatus = useAppSelector(
    selectImportContractDataStatus,
  );
  const updatedCount = useAppSelector(selectUpdatedCount);
  const uploadProgress = useAppSelector(selectDriverPlanningsUploadProgress);
  const [isModalPlanningVisible, setIsModalPlanningVisible] = useState(false);
  const [
    isGenerateMonthlyReportModalVisible,
    setIsGenerateMonthlyReportModalVisible,
  ] = useState(false);
  const [
    isImportContractDataModalVisible,
    setIsImportContractDataModalVisible,
  ] = useState(false);

  const isNotProductionEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] !== "production";
  }, []);

  const processingToastIdRef = useRef<number | string | null>(null);

  const tabs = useMemo(() => {
    const base = [
      {
        name: "advanced_analytics",
        title: t("fleet.tab.advanced_analytics"),
      },
      ...(isNotProductionEnvironment()
        ? [
            {
              name: "vehicle_sessions",
              title: t("fleet.tab.vehicle_sessions"),
            },
          ]
        : []),
      {
        name: "history",
        title: t("fleet.tab.tripsHistory"),
      },
      {
        name: "monthly_reports",
        title: t("fleet.tab.monthly_reports"),
      },
    ];
    return base;
  }, [t, isNotProductionEnvironment]);

  useEffect(() => {
    if (selectedTab && params.tab !== selectedTab) {
      navigate(
        `/${firstPath}/fleet/${selectedTab}${
          params.id !== undefined ? `/${params.id}` : ""
        }`,
      );
    }
  }, [navigate, params.id, params.tab, selectedTab, firstPath]);

  useEffect(() => {
    if (company && advancedAnalyticListVehiclesStatus === "idle") {
      void dispatch(advancedAnalyticVehiclesAsync(company.uuid));
    }
  }, [dispatch, company, advancedAnalyticListVehiclesStatus]);

  useEffect(() => {
    if (company && !company.hasAdvancedAnalytics) {
      navigate(`/home`);
    }
  }, [company, navigate]);

  useEffect(() => {
    if (!isNotProductionEnvironment() && selectedTab === "vehicle_sessions") {
      setSelectedTab("advanced_analytics");
    }
  }, [isNotProductionEnvironment, selectedTab]);

  useEffect(() => {
    if (driverPlanningsStatus === "success") {
      // Fermer le toast "processing" s'il existe
      if (processingToastIdRef.current) {
        dismissToastDS(processingToastIdRef.current);
        processingToastIdRef.current = null;
      }
      triggerToastDS(t("trips.importPlanning.success") || "", "SUCCESS");
      dispatch(driverPlanningsSlice.actions.resetPlanningUploadStatus());
    } else if (driverPlanningsStatus === "failed") {
      // Fermer le toast "processing" s'il existe
      if (processingToastIdRef.current) {
        dismissToastDS(processingToastIdRef.current);
        processingToastIdRef.current = null;
      }
      triggerToastDS(t("trips.importPlanning.failure") || "", "ERROR");
      dispatch(driverPlanningsSlice.actions.resetPlanningUploadStatus());
    }
  }, [dispatch, driverPlanningsStatus, t]);

  useEffect(() => {
    if (updateVehicleRestitutionStatus === "success") {
      triggerToastDS(
        t("fleet.vehicleSessions.updateRestitution.success") || "",
        "SUCCESS",
      );
      dispatch(fleetSlice.actions.resetUpdateVehicleRestitutionStatus());
      dispatch(fleetSlice.actions.resetVehicleSessionsListStatus());
      dispatch(fleetSlice.actions.resetRestitutionPicturesStatus());
    } else if (updateVehicleRestitutionStatus === "failed") {
      triggerToastDS(
        t("fleet.vehicleSessions.updateRestitution.failure") || "",
        "ERROR",
      );
      dispatch(fleetSlice.actions.resetUpdateVehicleRestitutionStatus());
    }
  }, [dispatch, updateVehicleRestitutionStatus, t]);

  useEffect(() => {
    if (driverPlanningsStatus === "processing") {
      if (!processingToastIdRef.current) {
        const toastId = triggerToastDS(
          t("trips.importPlanning.processing") || "",
          "DOWNLOADING",
          { autoClose: false },
        );
        if (toastId) {
          processingToastIdRef.current = toastId;
        }
      } else {
        updateToastDS(
          processingToastIdRef.current,
          t("trips.importPlanning.processing") || "",
          "DOWNLOADING",
          uploadProgress,
        );
      }
    }
  }, [driverPlanningsStatus, t, uploadProgress]);

  useEffect(() => {
    if (generateMonthlyMileageGapReportStatus === "success") {
      triggerToastDS(
        t("fleet.monthlyReports.generateModal.success") || "",
        "SUCCESS",
      );
      dispatch(fleetSlice.actions.resetGenerateMonthlyMileageGapReportStatus());
    } else if (generateMonthlyMileageGapReportStatus === "failed") {
      triggerToastDS(
        t("fleet.monthlyReports.generateModal.failure") || "",
        "ERROR",
      );
      dispatch(fleetSlice.actions.resetGenerateMonthlyMileageGapReportStatus());
    }
  }, [dispatch, generateMonthlyMileageGapReportStatus, t]);

  useEffect(() => {
    if (importContractDataStatus === "success") {
      triggerToastDS(
        t("fleet.advanced_analytics.importContract.success", {
          count: updatedCount ?? 0,
        }) || "",
        "SUCCESS",
      );
      dispatch(resetImportContractDataStatus());
      dispatch(fleetSlice.actions.resetFleetListStatus());
    } else if (importContractDataStatus === "failed") {
      triggerToastDS(
        t("fleet.advanced_analytics.importContract.failure") || "",
        "ERROR",
      );
      dispatch(resetImportContractDataStatus());
    }
  }, [dispatch, importContractDataStatus, t, updatedCount]);

  const canGenerateReports = useMemo(
    () => manager?.role === "OWNER" || manager?.role === "ADMIN",
    [manager],
  );

  const planningCta = {
    title: t("trips.cta.addPlanning"),
    action: () => {
      setIsModalPlanningVisible(true);
    },
    leftIcon: <UploadFileOutlined />,
  };

  const importContractDataCta =
    selectedTab === "advanced_analytics"
      ? {
          title: t("fleet.advanced_analytics.importContract.button"),
          action: () => {
            setIsImportContractDataModalVisible(true);
          },
          leftIcon: <UploadFileOutlined />,
        }
      : undefined;
  const monthlyReportsCta = {
    title: t("fleet.cta.generateReports"),
    action: () => {
      setIsGenerateMonthlyReportModalVisible(true);
    },
  };

  return (
    <>
      <ToastContainerDS />
      <ImportDriverPlanningsModal
        visible={isModalPlanningVisible}
        onCloseModal={() => {
          setIsModalPlanningVisible(false);
        }}
      />
      <ImportContractDataModal
        visible={isImportContractDataModalVisible}
        onCloseModal={() => {
          setIsImportContractDataModalVisible(false);
        }}
      />
      <GenerateMonthlyReportModal
        visible={isGenerateMonthlyReportModalVisible}
        onCloseModal={() => {
          setIsGenerateMonthlyReportModalVisible(false);
        }}
      />
      {!isModalPlanningVisible && !isImportContractDataModalVisible && (
        <PageDS
          title={t("fleet.title")}
          toaster={<ToastContainerDS />}
          cta={
            selectedTab === "monthly_reports" && canGenerateReports
              ? monthlyReportsCta
              : undefined
          }
          cta2={
            selectedTab === "history" || selectedTab === "monthly_reports"
              ? planningCta
              : importContractDataCta
          }
          topElement={
            <>
              <TabMenu
                tabs={tabs}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
              />
              <Spacer y={0.5} />
            </>
          }
          supportButton={{
            showMail: !otherSupportPhones,
            otherPhones: otherSupportPhones,
          }}
        >
          <AnalyticsVehiclesTab />
          {isNotProductionEnvironment() ? <VehicleSessionsTab /> : <></>}
          <HistoryTab />
          <MonthlyReportsTab canGenerateReports={canGenerateReports} />
        </PageDS>
      )}
    </>
  );
};
